import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { ArrowUpRight, Sparkles, type LucideIcon } from "lucide-react";
import { useBrand } from "@/branding/BrandProvider";
import { Kbd } from "./ui/kbd";
import { useChat } from "./chat/ChatProvider";
import { useModules } from "@/auth/ModulesContext";
import { AGENT_AREAS, ORG_AREA, type NavPage } from "@/config/nav";

interface CommandMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const CommandMenuContext = createContext<CommandMenuContextValue | null>(null);

export function useCommandMenu() {
  const ctx = useContext(CommandMenuContext);
  if (!ctx) throw new Error("useCommandMenu must be used within CommandMenuProvider");
  return ctx;
}

interface CommandNavItem {
  label: string;
  icon: LucideIcon;
  to: string;
  keywords?: string[];
}

function toCommandItem(page: NavPage): CommandNavItem {
  return {
    label: page.commandLabel ?? page.label,
    icon: page.icon,
    to: page.commandTo ?? page.to,
    keywords: page.commandKeywords,
  };
}

export function CommandMenuProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <CommandMenuContext.Provider value={{ open, setOpen }}>
      {children}
      <CommandMenuDialog open={open} onOpenChange={setOpen} />
    </CommandMenuContext.Provider>
  );
}

function CommandMenuDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const brand = useBrand();
  const { openChat, sendMessage } = useChat();
  const { hasModule, holdsModule } = useModules();
  const [query, setQuery] = useState("");
  const returnFocus = useRef<HTMLElement | null>(null);
  const focusChat = useRef(false);

  useEffect(() => {
    if (open) {
      focusChat.current = false;
      setQuery("");
    }
  }, [open]);

  const askAgent = useCallback(
    (q: string) => {
      const trimmed = q.trim();
      if (!trimmed) return;
      focusChat.current = true;
      onOpenChange(false);
      openChat();
      // Fire and forget; the chat panel surfaces streaming + errors
      void sendMessage(trimmed);
    },
    [onOpenChange, openChat, sendMessage],
  );

  const go = useCallback(
    (to: string) => {
      onOpenChange(false);
      navigate(to);
    },
    [navigate, onOpenChange],
  );

  const trimmed = query.trim();

  // Unheld modules are left out, not disabled: every page behind one answers 403. Home and sidebar explain the lock.
  const liveAreas = useMemo(
    () =>
      AGENT_AREAS.flatMap((area) =>
        area.kind === "live" && (area.hiddenWithoutModule ? holdsModule(area.module) : hasModule(area.module))
          ? [
              {
                id: area.id,
                label: area.label,
                items: area.pages
                  .filter((page) => !page.module || hasModule(page.module))
                  .map(toCommandItem),
              },
            ]
          : [],
      ),
    [hasModule, holdsModule],
  );
  // Same rule: an org destination behind an unheld module is left out.
  const orgItems = useMemo(
    () =>
      ORG_AREA.pages
        .filter((item) => !item.module || hasModule(item.module))
        .map(toCommandItem),
    [hasModule],
  );

  return (
    <CommandDialog
      className="sm:max-w-3xl"
      open={open}
      onOpenChange={onOpenChange}
      title="Command menu"
      description="Search pages or ask the agent."
      onOpenAutoFocus={() => { returnFocus.current = document.activeElement as HTMLElement | null; }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        if (focusChat.current) {
          const composer = Array.from(document.querySelectorAll<HTMLTextAreaElement>("[data-sandbox-chat] textarea"))
            .find((element) => element.getClientRects().length > 0 && !element.disabled);
          const panel = Array.from(document.querySelectorAll<HTMLElement>("[data-sandbox-chat]"))
            .find((element) => element.getClientRects().length > 0);
          (composer ?? panel)?.focus();
        } else if (returnFocus.current?.isConnected) {
          returnFocus.current.focus();
        }
      }}
    >
      <CommandInput
        aria-label="Search pages or ask the agent"
        placeholder="Ask agent or jump to…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="command-results max-h-[min(55dvh,520px)]">
        <CommandEmpty>No matches.</CommandEmpty>

        {trimmed.length > 0 && (
          <CommandGroup heading="Agent">
            <CommandItem
              animated
              className="my-1 rounded-lg border border-border/80 bg-muted/40"
              value={`ask-agent-${trimmed}`}
              onSelect={() => askAgent(trimmed)}
            >
              <Sparkles className="text-accent-blue" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-foreground">{trimmed}</span>
                <span className="text-xs text-muted-foreground">Ask {brand.name} to help</span>
              </span>
              <ArrowUpRight className="shrink-0 text-muted-foreground" />
            </CommandItem>
          </CommandGroup>
        )}

        {liveAreas.map((area, idx) => (
          <Fragment key={area.id}>
            {idx > 0 && <CommandSeparator />}
            <CommandGroup heading={area.label}>
              {area.items.map((item) => (
                <NavCommandItem key={item.to} item={item} onSelect={go} />
              ))}
            </CommandGroup>
          </Fragment>
        ))}

        <CommandSeparator />

        <CommandGroup heading="Organisation">
          {orgItems.map((item) => (
            <NavCommandItem key={item.to} item={item} onSelect={go} />
          ))}
        </CommandGroup>
      </CommandList>
      <div className="flex items-center justify-between gap-3 border-t bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-2"><Sparkles className="size-3.5" /> Pages and your agent, in one place</span>
        <span className="hidden items-center gap-3 sm:flex"><span><Kbd>↑</Kbd> <Kbd>↓</Kbd> navigate</span><span><Kbd>↵</Kbd> select</span></span>
      </div>
    </CommandDialog>
  );
}

function NavCommandItem({
  item,
  onSelect,
}: {
  item: CommandNavItem;
  onSelect: (to: string) => void;
}) {
  const Icon = item.icon;
  return (
    <CommandItem
      animated
      value={`${item.label} ${item.keywords?.join(" ") ?? ""}`}
      onSelect={() => onSelect(item.to)}
    >
      <Icon />
      <span>{item.label}</span>
    </CommandItem>
  );
}
